import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SlidersHorizontal, ChevronDown, X } from 'lucide-react';
import type { GarmentFilters, GarmentCategory, GarmentColor, GarmentSeason, GarmentMaterial, GarmentPattern } from '@/types';

interface FilterBarProps {
  filters: GarmentFilters;
  onFilterChange: (filters: Partial<GarmentFilters>) => void;
  onReset: () => void;
  className?: string;
}

const categories: { value: GarmentCategory; label: string }[] = [
  { value: 'top', label: 'Tops' },
  { value: 'bottom', label: 'Bottoms' },
  { value: 'dress', label: 'Dresses' },
  { value: 'outerwear', label: 'Outerwear' },
  { value: 'shoes', label: 'Shoes' },
  { value: 'accessory', label: 'Accessories' },
  { value: 'bag', label: 'Bags' },
  { value: 'activewear', label: 'Activewear' },
  { value: 'suit', label: 'Suits' },
];

const colors: { value: GarmentColor; label: string; hex: string }[] = [
  { value: 'black', label: 'Black', hex: '#1A1A1A' },
  { value: 'white', label: 'White', hex: '#FFFFFF' },
  { value: 'gray', label: 'Gray', hex: '#9CA3AF' },
  { value: 'red', label: 'Red', hex: '#EF4444' },
  { value: 'blue', label: 'Blue', hex: '#3B82F6' },
  { value: 'green', label: 'Green', hex: '#22C55E' },
  { value: 'yellow', label: 'Yellow', hex: '#EAB308' },
  { value: 'orange', label: 'Orange', hex: '#F97316' },
  { value: 'purple', label: 'Purple', hex: '#8B5CF6' },
  { value: 'pink', label: 'Pink', hex: '#EC4899' },
  { value: 'brown', label: 'Brown', hex: '#92400E' },
  { value: 'beige', label: 'Beige', hex: '#D4B896' },
  { value: 'navy', label: 'Navy', hex: '#1E3A5F' },
  { value: 'burgundy', label: 'Burgundy', hex: '#800020' },
];

const seasons: { value: GarmentSeason; label: string }[] = [
  { value: 'spring', label: 'Spring' },
  { value: 'summer', label: 'Summer' },
  { value: 'fall', label: 'Fall' },
  { value: 'winter', label: 'Winter' },
  { value: 'all', label: 'All Season' },
];

const materials: { value: GarmentMaterial; label: string }[] = [
  { value: 'cotton', label: 'Cotton' },
  { value: 'polyester', label: 'Polyester' },
  { value: 'silk', label: 'Silk' },
  { value: 'wool', label: 'Wool' },
  { value: 'linen', label: 'Linen' },
  { value: 'denim', label: 'Denim' },
  { value: 'leather', label: 'Leather' },
  { value: 'cashmere', label: 'Cashmere' },
  { value: 'knit', label: 'Knit' },
];

const patterns: { value: GarmentPattern; label: string }[] = [
  { value: 'solid', label: 'Solid' },
  { value: 'stripes', label: 'Stripes' },
  { value: 'plaid', label: 'Plaid' },
  { value: 'floral', label: 'Floral' },
  { value: 'polka-dot', label: 'Polka Dot' },
  { value: 'geometric', label: 'Geometric' },
];

const sortOptions = [
  { value: 'recent', label: 'Most Recent' },
  { value: 'name', label: 'Name' },
  { value: 'most-worn', label: 'Most Worn' },
  { value: 'least-worn', label: 'Least Worn' },
  { value: 'color', label: 'Color' },
] as const;

export function FilterBar({
  filters,
  onFilterChange,
  onReset,
  className = '',
}: FilterBarProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const activeFilterCount = [
    filters.category,
    filters.color,
    filters.season,
    filters.material,
    filters.pattern,
  ].filter(Boolean).length;

  return (
    <div className={className}>
      {/* Quick filter pills */}
      <div className="flex items-center gap-2 overflow-x-auto scrollbar-none py-1">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium
                     transition-colors flex-shrink-0 ${
                       activeFilterCount > 0
                         ? 'bg-vestir-purple text-white'
                         : 'bg-vestir-gray text-vestir-black'
                     }`}
          aria-expanded={isExpanded}
          aria-label="Toggle filters"
        >
          <SlidersHorizontal size={14} />
          Filters
          {activeFilterCount > 0 && (
            <span className="w-4 h-4 bg-white text-vestir-purple rounded-full text-[10px]
                           flex items-center justify-center font-bold">
              {activeFilterCount}
            </span>
          )}
        </button>

        {/* Category quick pills */}
        {categories.map((cat) => (
          <button
            key={cat.value}
            onClick={() =>
              onFilterChange({
                category: filters.category === cat.value ? undefined : cat.value,
              })
            }
            className={`px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap
                       transition-colors flex-shrink-0 ${
                         filters.category === cat.value
                           ? 'bg-vestir-purple text-white'
                           : 'bg-vestir-gray/60 text-vestir-soft-black'
                       }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Expanded filters */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-3 space-y-4 overflow-hidden"
          >
            {/* Color */}
            <div>
              <label className="text-xs font-medium text-vestir-gray-dark mb-2 block">
                Color
              </label>
              <div className="flex flex-wrap gap-2">
                {colors.map((c) => (
                  <button
                    key={c.value}
                    onClick={() =>
                      onFilterChange({
                        color: filters.color === c.value ? undefined : c.value,
                      })
                    }
                    className={`w-7 h-7 rounded-full border-2 transition-all ${
                      filters.color === c.value
                        ? 'border-vestir-purple scale-110'
                        : 'border-transparent'
                    }`}
                    style={{ backgroundColor: c.hex }}
                    aria-label={c.label}
                    title={c.label}
                  />
                ))}
              </div>
            </div>

            {/* Season */}
            <div>
              <label className="text-xs font-medium text-vestir-gray-dark mb-2 block">
                Season
              </label>
              <div className="flex flex-wrap gap-2">
                {seasons.map((s) => (
                  <button
                    key={s.value}
                    onClick={() =>
                      onFilterChange({
                        season: filters.season === s.value ? undefined : s.value,
                      })
                    }
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      filters.season === s.value
                        ? 'bg-vestir-purple text-white'
                        : 'bg-vestir-gray/60 text-vestir-soft-black'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Material */}
            <div>
              <label className="text-xs font-medium text-vestir-gray-dark mb-2 block">
                Material
              </label>
              <div className="flex flex-wrap gap-2">
                {materials.map((m) => (
                  <button
                    key={m.value}
                    onClick={() =>
                      onFilterChange({
                        material:
                          filters.material === m.value ? undefined : m.value,
                      })
                    }
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      filters.material === m.value
                        ? 'bg-vestir-purple text-white'
                        : 'bg-vestir-gray/60 text-vestir-soft-black'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Pattern */}
            <div>
              <label className="text-xs font-medium text-vestir-gray-dark mb-2 block">
                Pattern
              </label>
              <div className="flex flex-wrap gap-2">
                {patterns.map((p) => (
                  <button
                    key={p.value}
                    onClick={() =>
                      onFilterChange({
                        pattern:
                          filters.pattern === p.value ? undefined : p.value,
                      })
                    }
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      filters.pattern === p.value
                        ? 'bg-vestir-purple text-white'
                        : 'bg-vestir-gray/60 text-vestir-soft-black'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Sort */}
            <div>
              <label className="text-xs font-medium text-vestir-gray-dark mb-2 block">
                Sort by
              </label>
              <div className="relative">
                <select
                  value={filters.sortBy}
                  onChange={(e) =>
                    onFilterChange({
                      sortBy: e.target.value as GarmentFilters['sortBy'],
                    })
                  }
                  className="input-field appearance-none pr-8"
                  aria-label="Sort order"
                >
                  {sortOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={16}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-vestir-gray-dark pointer-events-none"
                />
              </div>
            </div>

            {/* Reset */}
            {activeFilterCount > 0 && (
              <button
                onClick={onReset}
                className="flex items-center gap-1 text-sm text-vestir-coral font-medium"
              >
                <X size={14} />
                Clear all filters
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
